import React, { useState, useEffect } from 'react';
import { useAuth } from "../contexts/AuthContext";
import PerformanceDataTester from "../components/test/PerformanceDataTester";
import { 
  Building2, 
  Plus, 
  Search, 
  Filter, 
  Edit3, 
  Trash2, 
  Phone, 
  Mail, 
  MapPin, 
  Globe, 
  User, 
  Building, 
  X,
  Save,
  AlertCircle,
  CheckCircle,
  Home,
  Euro,
  TrendingUp,
  TrendingDown,
  Users,
  PieChart,
  BarChart3,
  Target,
  Zap,
  Calendar,
  FileText,
  Award,
  Activity,
  AlertTriangle,
  Shield,
  Calculator
} from 'lucide-react';
import { masterSupabase as supabase } from '../lib/supabase-master.js';

const OwnersPage = () => {
  const { user } = useAuth();
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingOwner, setEditingOwner] = useState(null);
  const [notification, setNotification] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [viewMode, setViewMode] = useState('klassisch');

  // Formatierungsfunktionen
  const formatCurrency = (amount) => {
    if (!amount) return '€0';
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (number) => {
    if (!number) return '0';
    return new Intl.NumberFormat('de-DE').format(number);
  };

  const formatPercentage = (percentage) => {
    if (!percentage) return '0,0%';
    return new Intl.NumberFormat('de-DE', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(percentage / 100);
  };

  // Daten laden
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadOwners(),
        loadProperties(),
        loadTenants()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      showNotification('Fehler beim Laden der Daten', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadOwners = async () => {
    try {
      const { data, error } = await supabase
        .from('owners')
        .select('*')
        .order('name');
      
      if (error) throw error;
      setOwners(data || []);
    } catch (error) {
      console.error('Error loading owners:', error);
    }
  };

  const loadProperties = async () => {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .order('address');
      
      if (error) throw error;
      setProperties(data || []);
    } catch (error) {
      console.error('Error loading properties:', error);
    }
  };

  const loadTenants = async () => {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select('*')
        .order('name');
      
      if (error) throw error;
      setTenants(data || []);
    } catch (error) {
      console.error('Error loading tenants:', error);
    }
  };

  // Berechnungsfunktionen
  const calculateOwnerPerformance = (ownerId) => {
    const ownerProperties = properties.filter(p => p.owner_id === ownerId);
    const ownerTenants = tenants.filter(t => 
      ownerProperties.some(p => p.id === t.property_id)
    );

    if (ownerProperties.length === 0) {
      return {
        grossYield: 0,
        netYield: 0,
        totalValue: 0,
        monthlyRent: 0,
        annualRent: 0,
        netCashflow: 0,
        occupancyRate: 0,
        performanceRating: { icon: '⭐', color: 'text-gray-400', label: 'Keine Daten' }
      };
    }

    // Berechne Werte basierend auf echten Tenant-Daten
    const totalValue = ownerProperties.reduce((sum, p) => sum + (p.purchase_price || 0), 0);
    const monthlyRent = ownerTenants.reduce((sum, t) => sum + (t.monthly_rent || 0), 0);
    const annualRent = monthlyRent * 12;

    // Echte Kosten aus Properties
    const totalOperatingCosts = ownerProperties.reduce((sum, p) => 
      sum + (p.operating_costs || 0) + (p.maintenance_costs || 0), 0
    );
    
    const grossYield = totalValue > 0 ? (annualRent / totalValue) * 100 : 0;
    const netCashflow = annualRent - totalOperatingCosts;
    const netYield = totalValue > 0 ? (netCashflow / totalValue) * 100 : 0;

    // Korrekte Auslastungsberechnung
    const totalUnits = ownerProperties.reduce((sum, p) => sum + (p.total_units || 1), 0);
    const rentedUnits = ownerTenants.filter(t => t.lease_end_date > new Date() || !t.lease_end_date).length;
    const occupancyRate = totalUnits > 0 ? (rentedUnits / totalUnits) * 100 : 0;

    // Performance Rating
    let performanceRating;
    if (grossYield >= 8) {
      performanceRating = { icon: '🌟', color: 'text-green-600', label: 'Exzellent' };
    } else if (grossYield >= 6) {
      performanceRating = { icon: '⭐', color: 'text-blue-600', label: 'Sehr gut' };
    } else if (grossYield >= 4) {
      performanceRating = { icon: '⚡', color: 'text-yellow-600', label: 'Gut' };
    } else if (grossYield >= 2) {
      performanceRating = { icon: '📈', color: 'text-orange-600', label: 'Durchschnitt' };
    } else {
      performanceRating = { icon: '📉', color: 'text-red-600', label: 'Unterdurchschnitt' };
    }

    return {
      grossYield,
      netYield,
      totalValue,
      monthlyRent,
      annualRent,
      netCashflow,
      occupancyRate,
      performanceRating
    };
  };

  const calculateOwnerRisk = (ownerId) => {
    const ownerProperties = properties.filter(p => p.owner_id === ownerId);
    const ownerTenants = tenants.filter(t => 
      ownerProperties.some(p => p.id === t.property_id)
    );

    if (ownerProperties.length === 0) {
      return {
        diversificationRisk: 'Hoch',
        tenantConcentration: 'Hoch',
        vacancyHistory: 'Unbekannt',
        overallRisk: 'Hoch',
        propertyCount: 0,
        riskFactors: []
      };
    }

    // Diversifikationsrisiko
    let diversificationRisk;
    if (ownerProperties.length >= 5) {
      diversificationRisk = 'Niedrig';
    } else if (ownerProperties.length >= 3) {
      diversificationRisk = 'Mittel';
    } else {
      diversificationRisk = 'Hoch';
    }

    // Mieter-Konzentration (Klumpenrisiko)
    const totalRent = ownerTenants.reduce((sum, t) => sum + (t.monthly_rent || 0), 0);
    const maxTenantRent = Math.max(...ownerTenants.map(t => t.monthly_rent || 0));
    const tenantConcentrationRatio = totalRent > 0 ? (maxTenantRent / totalRent) * 100 : 0;
    
    let tenantConcentration;
    if (tenantConcentrationRatio <= 30) {
      tenantConcentration = 'Niedrig';
    } else if (tenantConcentrationRatio <= 50) {
      tenantConcentration = 'Mittel';
    } else {
      tenantConcentration = 'Hoch';
    }

    // Leerstand-Historie (vereinfacht)
    const currentVacancy = ownerProperties.length - ownerTenants.filter(t => 
      t.lease_end_date > new Date() || !t.lease_end_date
    ).length;
    const vacancyRate = (currentVacancy / ownerProperties.length) * 100;
    
    let vacancyHistory;
    if (vacancyRate === 0) {
      vacancyHistory = 'Niedrig';
    } else if (vacancyRate <= 10) {
      vacancyHistory = 'Mittel';
    } else {
      vacancyHistory = 'Hoch';
    }

    // Gesamtrisiko
    const riskScore = 
      (diversificationRisk === 'Niedrig' ? 1 : diversificationRisk === 'Mittel' ? 2 : 3) +
      (tenantConcentration === 'Niedrig' ? 1 : tenantConcentration === 'Mittel' ? 2 : 3) +
      (vacancyHistory === 'Niedrig' ? 1 : vacancyHistory === 'Mittel' ? 2 : 3);

    let overallRisk;
    if (riskScore <= 4) {
      overallRisk = 'Niedrig';
    } else if (riskScore <= 7) {
      overallRisk = 'Mittel';
    } else {
      overallRisk = 'Hoch';
    }

    const riskFactors = [
      `${ownerProperties.length} Objekte`,
      `${Math.round(tenantConcentrationRatio)}% größter Mieter`,
      `${Math.round(vacancyRate)}% Leerstand`
    ];

    return {
      diversificationRisk,
      tenantConcentration,
      vacancyHistory,
      overallRisk,
      propertyCount: ownerProperties.length,
      riskFactors
    };
  };

  const calculateOwnerTaxes = (ownerId) => {
    const performance = calculateOwnerPerformance(ownerId);
    
    if (performance.netCashflow <= 0) {
      return {
        taxRate: 0,
        taxableIncome: 0,
        annualTax: 0,
        afterTaxCashflow: performance.netCashflow
      };
    }

    // Vereinfachte Steuerberechnung (42% Spitzensteuersatz)
    const taxRate = 42;
    const taxableIncome = performance.netCashflow;
    const annualTax = taxableIncome * (taxRate / 100);
    const afterTaxCashflow = performance.netCashflow - annualTax;

    return {
      taxRate,
      taxableIncome,
      annualTax,
      afterTaxCashflow
    };
  };

  const calculateMarketAnalysis = (ownerId) => {
    const ownerProperties = properties.filter(p => p.owner_id === ownerId);
    const ownerTenants = tenants.filter(t => 
      ownerProperties.some(p => p.id === t.property_id)
    );

    if (ownerProperties.length === 0 || ownerTenants.length === 0) {
      return {
        avgMarketPosition: 1.0,
        potentialLabel: 'Keine Daten',
        marketRisk: 'Unbekannt'
      };
    }

    // Berechne durchschnittliche Miete pro m²
    const totalRent = ownerTenants.reduce((sum, t) => sum + (t.monthly_rent || 0), 0);
    const totalArea = ownerProperties.reduce((sum, p) => sum + (p.rentable_area || 50), 0); // Fallback 50m²
    const avgRentPerSqm = totalArea > 0 ? totalRent / totalArea : 0;

    // Simulierter Marktmedian (€12/m² als Referenz)
    const marketMedian = 12.0;
    const marketPosition = avgRentPerSqm / marketMedian;

    let potentialLabel, marketRisk;
    if (marketPosition < 0.85) {
      potentialLabel = 'Ungenutztes Mietpotenzial';
      marketRisk = 'Niedrig';
    } else if (marketPosition > 1.15) {
      potentialLabel = 'Erhöhtes Sozialrisiko';
      marketRisk = 'Hoch';
    } else {
      potentialLabel = 'Marktgerecht';
      marketRisk = 'Niedrig';
    }

    return {
      avgMarketPosition: marketPosition,
      potentialLabel,
      marketRisk
    };
  };

  // Globale Statistiken
  const calculateGlobalStats = () => {
    const totalValue = properties.reduce((sum, p) => sum + (p.purchase_price || 0), 0);
    const totalMonthlyRent = tenants.reduce((sum, t) => sum + (t.monthly_rent || 0), 0);
    const totalAnnualRent = totalMonthlyRent * 12;
    const totalUnits = properties.reduce((sum, p) => sum + (p.total_units || 1), 0);
    const occupiedUnits = tenants.filter(t => t.lease_end_date > new Date() || !t.lease_end_date).length;
    const occupancyRate = totalUnits > 0 ? (occupiedUnits / totalUnits) * 100 : 0;

    return {
      totalValue,
      totalMonthlyRent,
      totalAnnualRent,
      occupancyRate,
      totalProperties: properties.length,
      totalOwners: owners.length
    };
  };

  // Modal und Formular Funktionen
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleAddOwner = () => {
    setEditingOwner(null);
    setShowModal(true);
  };

  const handleEditOwner = (owner) => {
    setEditingOwner(owner);
    setShowModal(true);
  };

  const handleDeleteOwner = async (ownerId) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diesen Eigentümer löschen möchten?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('owners')
        .delete()
        .eq('id', ownerId);

      if (error) throw error;
      
      await loadOwners();
      showNotification('Eigentümer erfolgreich gelöscht');
    } catch (error) {
      console.error('Error deleting owner:', error);
      showNotification('Fehler beim Löschen des Eigentümers', 'error');
    }
  };

  const handleSaveOwner = async (ownerData) => {
    try {
      // Map frontend fields to database schema
      const { company, name, ...restData } = ownerData;
      const dataToSave = {
        ...restData,
        name: company,
        contact_person: name,
        street: ownerData.street,
        postal_code: ownerData.postal_code,
        city: ownerData.city,
        created_by: user.id
      };
      
      if (editingOwner) {
        const { error } = await supabase
          .from('owners')
          .update(dataToSave)
          .eq('id', editingOwner.id);
        
        if (error) throw error;
        showNotification('Eigentümer erfolgreich aktualisiert');
      } else {
        const { error } = await supabase
          .from('owners')
          .insert([dataToSave]);
        
        if (error) throw error;
        showNotification('Eigentümer erfolgreich hinzugefügt');
      }
      
      setShowModal(false);
      await loadOwners();
    } catch (error) {
      console.error('Error saving owner:', error);
      showNotification('Fehler beim Speichern des Eigentümers', 'error');
    }
  };

  // Filter und Suche
  const filteredOwners = owners.filter(owner => {
    const matchesSearch = owner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (owner.email && owner.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (owner.company && owner.company.toLowerCase().includes(searchTerm.toLowerCase()));
    
    if (filterType === 'all') return matchesSearch;
    
    const ownerProperties = properties.filter(p => p.owner_id === owner.id);
    if (filterType === 'with_properties') return matchesSearch && ownerProperties.length > 0;
    if (filterType === 'without_properties') return matchesSearch && ownerProperties.length === 0;
    
    return matchesSearch;
  });

  const globalStats = calculateGlobalStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <User className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Eigentümer-Verwaltung
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Verwalten Sie Ihre Eigentümer und analysieren Sie deren Portfolio-Performance
            </p>
          </div>
        </div>
        <button
          onClick={handleAddOwner}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Neuer Eigentümer
        </button>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {[
              { id: 'overview', label: 'Übersicht', icon: BarChart3 },
              { id: 'performance', label: 'Performance', icon: TrendingUp },
              { id: 'risk', label: 'Risiko', icon: Shield },
              { id: 'taxes', label: 'Steueranalyse', icon: Calculator }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="flex flex-1 gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Eigentümer suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
          >
            <option value="all">Alle Typen</option>
            <option value="with_properties">Mit Objekten</option>
            <option value="without_properties">Ohne Objekte</option>
          </select>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('klassisch')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'klassisch'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Klassisch
          </button>
          <button
            onClick={() => setViewMode('kompakt')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'kompakt'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Kompakt
          </button>
        </div>
      </div>

      {/* Global Stats */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Gesamtwert</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(globalStats.totalValue)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-2 rounded-lg flex-shrink-0 ml-3">
                <Home className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">Portfolio-Wert</p>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Jahresmiete</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(globalStats.totalAnnualRent)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-green-600 p-2 rounded-lg flex-shrink-0 ml-3">
                <Euro className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-xs text-green-600 dark:text-green-400 mt-2">Brutto p.a.</p>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Auslastung</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatPercentage(globalStats.occupancyRate)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-2 rounded-lg flex-shrink-0 ml-3">
                <Users className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-xs text-purple-600 dark:text-purple-400 mt-2">Vermietungsquote</p>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Rendite</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {globalStats.totalValue > 0 ? formatPercentage((globalStats.totalAnnualRent / globalStats.totalValue) * 100) : '0,0%'}
                </p>
              </div>
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-2 rounded-lg flex-shrink-0 ml-3">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-2">Brutto p.a.</p>
          </div>
        </div>
      )}

      {/* Owners List */}
      <div className="space-y-6">
        {viewMode === 'klassisch' ? (
          /* Klassische Ansicht */
          filteredOwners.map(owner => {
            const ownerProperties = properties.filter(p => p.owner_id === owner.id);
            const ownerTenants = tenants.filter(t => 
              ownerProperties.some(p => p.id === t.property_id)
            );
            const performance = calculateOwnerPerformance(owner.id);
            const risk = calculateOwnerRisk(owner.id);
            const taxes = calculateOwnerTaxes(owner.id);
            const marketAnalysis = calculateMarketAnalysis(owner.id);

            return (
              <div key={owner.id} className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                {/* Owner Header */}
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                        <User className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                          {owner.name}
                        </h3>
                        {owner.company && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">{owner.company}</p>
                        )}
                        <p className="text-sm text-gray-500 dark:text-gray-500">{owner.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditOwner(owner)}
                        className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteOwner(owner.id)}
                        className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Portfolio</p>
                      <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                        {formatCurrency(performance.totalValue)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Monatsmiete</p>
                      <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                        {formatCurrency(performance.monthlyRent)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Rendite</p>
                      <p className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">
                        {formatPercentage(performance.grossYield)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Auslastung</p>
                      <p className="text-lg font-semibold text-purple-600 dark:text-purple-400">
                        {formatPercentage(performance.occupancyRate)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Immobilien-Portfolio */}
                <div className="p-6">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Immobilien-Portfolio</h4>
                  {ownerProperties.length > 0 ? (
                    <div className="space-y-3">
                      {ownerProperties.map(property => (
                        <div key={property.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Home className="w-5 h-5 text-gray-400" />
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {property.address}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {property.property_type} • {property.total_units || 1} Einheit(en)
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {formatCurrency(property.purchase_price)}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Kaufpreis
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                      Keine Immobilien zugeordnet
                    </p>
                  )}
                </div>

                {/* Tab Content */}
                {activeTab === 'performance' && performance && (
                  <div className="p-6 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Performance-Analyse</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Brutto-Rendite</p>
                          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                            {formatPercentage(performance.grossYield)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Netto-Rendite</p>
                          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                            {formatPercentage(performance.netYield)}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Netto-Cashflow</p>
                          <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                            {formatCurrency(performance.netCashflow)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Auslastung</p>
                          <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                            {formatPercentage(performance.occupancyRate)}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Performance-Rating</p>
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{performance.performanceRating.icon}</span>
                            <span className={`font-semibold ${performance.performanceRating.color}`}>
                              {performance.performanceRating.label}
                            </span>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Marktposition</p>
                          <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                            marketAnalysis.marketRisk === 'Niedrig' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                            marketAnalysis.marketRisk === 'Hoch' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                            'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                          }`}>
                            {marketAnalysis.potentialLabel}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'risk' && risk && (
                  <div className="p-6 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Risiko-Analyse</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Diversifikation</span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            risk.diversificationRisk === 'Niedrig' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                            risk.diversificationRisk === 'Mittel' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                            'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                          }`}>
                            {risk.diversificationRisk}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{risk.propertyCount} Objekte</p>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Mieter-Konzentration</span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            risk.tenantConcentration === 'Niedrig' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                            risk.tenantConcentration === 'Mittel' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                            'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                          }`}>
                            {risk.tenantConcentration}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Klumpenrisiko</p>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Leerstand</span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            risk.vacancyHistory === 'Niedrig' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                            risk.vacancyHistory === 'Mittel' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                            'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                          }`}>
                            {risk.vacancyHistory}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Historie</p>
                      </div>
                    </div>
                    <div className="mt-6 p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900 dark:text-white">Gesamtrisiko</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          risk.overallRisk === 'Niedrig' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                          risk.overallRisk === 'Mittel' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                          'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                        }`}>
                          {risk.overallRisk}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {risk.riskFactors.map((factor, index) => (
                          <span key={index} className="text-xs bg-white dark:bg-gray-600 px-2 py-1 rounded text-gray-600 dark:text-gray-300">
                            {factor}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'taxes' && taxes && (
                  <div className="p-6 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Steuer-Analyse</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Steuersatz</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">
                          {formatPercentage(taxes.taxRate)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Zu versteuerndes Einkommen</p>
                        <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          {formatCurrency(taxes.taxableIncome)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Jährliche Steuer</p>
                        <p className="text-xl font-bold text-red-600 dark:text-red-400">
                          {formatCurrency(taxes.annualTax)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Cashflow nach Steuern</p>
                        <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(taxes.afterTaxCashflow)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          /* Kompakt-Ansicht */
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-slate-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Eigentümer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Portfolio-Wert
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Monatsmiete
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Rendite
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Auslastung
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Objekte
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Risiko
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Cashflow
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Marktposition
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Kontakt
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredOwners.map(owner => {
                    const ownerProperties = properties.filter(p => p.owner_id === owner.id);
                    const performance = calculateOwnerPerformance(owner.id);
                    const risk = calculateOwnerRisk(owner.id);
                    const marketAnalysis = calculateMarketAnalysis(owner.id);

                    return (
                      <tr key={owner.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                              <User className="w-5 h-5 text-white" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {owner.name}
                              </div>
                              {owner.company && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {owner.company}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatCurrency(performance.totalValue)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-green-600 dark:text-green-400">
                            {formatCurrency(performance.monthlyRent)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                              {formatPercentage(performance.grossYield)}
                            </span>
                            <span className="ml-2 text-lg">{performance.performanceRating.icon}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-purple-600 dark:text-purple-400">
                            {formatPercentage(performance.occupancyRate)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {ownerProperties.length}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            risk.overallRisk === 'Niedrig' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                            risk.overallRisk === 'Mittel' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                            'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                          }`}>
                            {risk.overallRisk}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                            {formatCurrency(performance.netCashflow)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {marketAnalysis && (
                            <div>
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                marketAnalysis.marketRisk === 'Niedrig' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                                marketAnalysis.marketRisk === 'Hoch' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                                'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                              }`}>
                                {marketAnalysis.potentialLabel === 'Ungenutztes Mietpotenzial' ? 'Potenzial' :
                                 marketAnalysis.potentialLabel === 'Erhöhtes Sozialrisiko' ? 'Risiko' : 'Normal'}
                              </span>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {Math.round(marketAnalysis.avgMarketPosition * 100)}% Median
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {owner.email}
                          </div>
                          {owner.phone && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {owner.phone}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditOwner(owner)}
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteOwner(owner.id)}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Owner Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {editingOwner ? 'Eigentümer bearbeiten' : 'Neuer Eigentümer'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <OwnerForm
              owner={editingOwner}
              onSave={handleSaveOwner}
              onCancel={() => setShowModal(false)}
            />
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className="fixed top-4 right-4 z-[60] max-w-sm w-full">
          <div className={`bg-white dark:bg-slate-900 shadow-lg rounded-xl border-l-4 p-4 ${
            notification.type === 'success' ? 'border-emerald-500' : 'border-red-500'
          }`}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {notification.type === 'success' ? (
                  <CheckCircle className="h-5 w-5 text-emerald-500" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-500" />
                )}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {notification.message}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Owner Form Component
const OwnerForm = ({ owner, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: owner?.contact_person || '',
    email: owner?.email || '',
    phone: owner?.phone || '',
    company: owner?.name || '',
    street: owner?.street || '',
    postal_code: owner?.postal_code || '',
    city: owner?.city || '',
    website: owner?.website || '',
    notes: owner?.notes || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Name *
          </label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            placeholder="Vollständiger Name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            E-Mail
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            placeholder="E-Mail-Adresse"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Telefon
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            placeholder="Telefonnummer"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Unternehmen
          </label>
          <input
            type="text"
            value={formData.company}
            onChange={(e) => handleChange('company', e.target.value)}
            className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            placeholder="Firmenname"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Straße
        </label>
        <input
          type="text"
          value={formData.street}
          onChange={(e) => handleChange('street', e.target.value)}
          className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
          placeholder="Straße und Hausnummer"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            PLZ
          </label>
          <input
            type="text"
            value={formData.postal_code}
            onChange={(e) => handleChange('postal_code', e.target.value)}
            className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            placeholder="12345"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Stadt
          </label>
          <input
            type="text"
            value={formData.city}
            onChange={(e) => handleChange('city', e.target.value)}
            className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            placeholder="Berlin"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Website
        </label>
        <input
          type="url"
          value={formData.website}
          onChange={(e) => handleChange('website', e.target.value)}
          className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
          placeholder="https://..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Notizen
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          rows={4}
          className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
          placeholder="Zusätzliche Notizen..."
        />
      </div>

      <div className="flex justify-end gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl font-medium transition-colors"
        >
          Abbrechen
        </button>
        <button
          type="submit"
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          Speichern
        </button>
      </div>
    </form>
  );
};

export default OwnersPage;

